import { PLAY_TYPES } from './payouts';

export const DEFAULT_SURGE_PLAY_THRESHOLD = 12;
export const DEFAULT_SURGE_CAP_RATIO = 0.75;

export interface NumberCapConfig {
  perNumberCap: number;
  numberCaps?: Record<string, number>;
  surgePlayThreshold?: number;
  surgeCapRatio?: number;
}

export interface PlayLike {
  id?: string;
  listerId?: string;
  type?: string;
  number?: string;
  n?: string;
  number2?: string | null;
  stake?: number;
  status?: string;
  window?: string;
  payout?: number;
}

/** Normalize a play number for cap lookups (2- or 3-digit). */
export function normalizePlayNumber(number: string, type?: string): string {
  if (!number) return '';
  if (type === PLAY_TYPES.CENTENA) return number.padStart(3, '0').slice(-3);
  return number.padStart(2, '0').slice(-2);
}

/** Numbers whose exposure ledger a play touches. */
export function numbersTouchedByPlay(dto: PlayLike): string[] {
  const type = dto.type ?? PLAY_TYPES.FIJO;
  const primary = normalizePlayNumber(dto.number ?? dto.n ?? '', type);
  if (!primary) return [];
  if (type === PLAY_TYPES.PARLE && dto.number2) {
    const second = normalizePlayNumber(dto.number2, type);
    return second && second !== primary ? [primary, second] : [primary];
  }
  return [primary];
}

/** Banker override or house default cap for a number. */
export function getBankerNumberCap(number: string, config: NumberCapConfig): number {
  const key = normalizePlayNumber(number);
  const override = config.numberCaps?.[key];
  return override != null ? Number(override) : config.perNumberCap;
}

function isOpenPlay(play: PlayLike): boolean {
  return !play.status || play.status === 'open';
}

function playTouchesNumber(play: PlayLike, number: string): boolean {
  return numbersTouchedByPlay(play).includes(normalizePlayNumber(number));
}

/** Stake attributed to a number for exposure (parlé splits evenly across legs). */
export function stakeContributionForNumber(dto: PlayLike, number: string): number {
  const key = normalizePlayNumber(number);
  const touched = numbersTouchedByPlay(dto);
  if (!touched.includes(key)) return 0;
  const stake = Number(dto.stake ?? 0);
  if (dto.type === PLAY_TYPES.PARLE && touched.length > 1) {
    return stake / touched.length;
  }
  return stake;
}

export function countOpenPlaysOnNumber(
  plays: PlayLike[],
  number: string,
  window?: string,
): number {
  return plays.filter((play) => {
    if (!isOpenPlay(play)) return false;
    if (window && play.window && play.window !== window) return false;
    return playTouchesNumber(play, number);
  }).length;
}

export function aggregateOpenStakeOnNumber(
  plays: PlayLike[],
  number: string,
  window?: string,
): number {
  return plays
    .filter((play) => isOpenPlay(play))
    .filter((play) => !window || !play.window || play.window === window)
    .reduce((sum, play) => sum + stakeContributionForNumber(play, number), 0);
}

export interface NumberExposure {
  cap: number;
  exposure: number;
  playCount: number;
  surgeActive: boolean;
}

/** Effective cap after banker overrides and surge rules. */
export function resolveNumberExposure(
  number: string,
  config: NumberCapConfig,
  plays: PlayLike[],
  window?: string,
): NumberExposure {
  const key = normalizePlayNumber(number);
  const baseCap = getBankerNumberCap(key, config);
  const playCount = countOpenPlaysOnNumber(plays, key, window);
  const threshold = config.surgePlayThreshold ?? DEFAULT_SURGE_PLAY_THRESHOLD;
  const ratio = config.surgeCapRatio ?? DEFAULT_SURGE_CAP_RATIO;
  const surgeActive = playCount >= threshold;
  const cap = surgeActive ? Math.floor(baseCap * ratio) : baseCap;
  const exposure = aggregateOpenStakeOnNumber(plays, key, window);

  return { cap, exposure, playCount, surgeActive };
}

/**
 * Returns an error key when the play would exceed number exposure limits.
 */
export function validateNumberExposure(
  dto: PlayLike,
  config: NumberCapConfig,
  existingPlays: PlayLike[] = [],
  window?: string,
): string | null {
  const plays = Array.isArray(existingPlays) ? existingPlays : [];

  for (const number of numbersTouchedByPlay(dto)) {
    const { cap, exposure, surgeActive } = resolveNumberExposure(number, config, plays, window);
    const contribution = stakeContributionForNumber(dto, number);
    if (exposure + contribution > cap) {
      if (surgeActive) return 'error_number_surge_cap';
      const key = normalizePlayNumber(number);
      if (config.numberCaps?.[key] != null) return 'error_number_cap_override';
      return 'error_number_cap_exceeded';
    }
  }

  return null;
}
